<template>
  <v-list-item
    class="booking-services-item"
    :class="{ 'booking-services-item-mobile': isMobile || !small }"
    outlined
    @click="$emit('click', item)"
  >
    <v-list-item-avatar class="booking-services-item-avatar" tile size="60">
      <v-img v-if="item.imageUrl" :src="item.imageUrl" style="border-radius: 4px" contain />
      <v-avatar v-else class="mb-8 mt-8" :color="color" size="80">
        <v-icon v-if="item.icon" dark x-large> mdi-{{ item.icon }} </v-icon>
        <v-icon v-else dark x-large> mdi-account-wrench-outline </v-icon>
      </v-avatar>
    </v-list-item-avatar>
    <v-list-item-content class="booking-services-item-content">
      <v-list-item-title>{{ item.name }}</v-list-item-title>
      <v-list-item-subtitle class="booking-services-item-description">
        {{ item.description }}
      </v-list-item-subtitle>
      <v-list-item-subtitle>
        <span v-if="item.isDisplayDuration" class="mx-1">
          {{ $t("bookingForm.common.minutes", { value: item.duration / 1000 / 60 }) }}
        </span>

        <v-chip v-if="hasCost" class="booking-services-item-cost mx-1" x-small label outlined color="gray">
          {{ formattedCost }}
        </v-chip>
      </v-list-item-subtitle>
    </v-list-item-content>
  </v-list-item>
</template>

<script lang="ts">
import { Component, Vue, Prop } from "vue-property-decorator";
import { BookingServiceBookingViewModelGen } from "@/services/booking/v1/data-contracts";
import { getDefaultPartnerColor } from "@/lib/getDefaultPartnerColor";
import { CurrencyEnum } from "@/lib/enum/currency.enum";
import { formatCurrency } from "@/lib/utility/currency-helper";

@Component({
  components: {}
})
export default class BookingServicesItem extends Vue {
  @Prop({ default: () => ({}) })
  item!: BookingServiceBookingViewModelGen;

  @Prop({ default: true })
  small!: boolean;

  get isMobile() {
    return this.$vuetify.breakpoint.mobile;
  }

  get color() {
    return getDefaultPartnerColor();
  }

  /**
   * Whether the service has a configured cost worth displaying to the customer.
   */
  get hasCost(): boolean {
    return (this.item?.cost ?? 0) > 0;
  }

  /**
   * The service cost formatted as EUR currency for display in the chip,
   * e.g. "15,00 €". Only meaningful when {@link hasCost} is true.
   */
  get formattedCost(): string {
    return formatCurrency(this.item?.cost ?? 0, CurrencyEnum.EUR);
  }
}
</script>

<style scoped>
.booking-services-item {
  transition: min-height 0.25s ease;
  align-items: flex-start;
}

.booking-services-item-description {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  max-height: 1.6em;
  transition: max-height 0.25s ease;
  line-height: 1.6;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.booking-services-item-mobile .booking-services-item-description {
  -webkit-line-clamp: unset;
  display: block;
  white-space: pre-line;
  max-height: none;
}

.booking-services-item:not(.booking-services-item-mobile):hover .booking-services-item-description,
.booking-services-item:not(.booking-services-item-mobile):focus-within .booking-services-item-description {
  -webkit-line-clamp: unset;
  display: block;
  white-space: pre-line;
  max-height: 18em;
}

.booking-services-item-avatar,
.booking-services-item-content {
  align-self: flex-start;
}

.booking-services-item-avatar {
  margin-top: 0.9rem;
}
</style>
